/**
 * LoginBoard Component
 *
 * Renders the game board background with door rectangle and "A Game of Analogies"
 * SVG overlay. Supports a two-faced flip within the door rectangle only:
 * front face shows auth content (children), back face shows post-auth content (backChildren).
 *
 * Background, logos, and footer remain static — only the door-rect panel flips.
 */

import { type ReactNode } from 'react';
import { useBoardLayout, BOARD_WIDTH, BOARD_HEIGHT } from '../hooks/useBoardLayout';
import BoardBackground from '../svg/BoardBackground';
import LogoLeft from '../svg/LogoLeft';
import LogoRight from '../svg/LogoRight';
import PlusSign from '../svg/PlusSign';
import AGameOfAnalogies from './AGameOfAnalogies';
import RevealDoorFull from '../svg/RevealDoorFull';
import styles from './LoginBoard.module.css';

interface LoginBoardProps {
  children: ReactNode;
  /** Content shown on the back face of the door rect (post-login) */
  backChildren?: ReactNode;
  /** Whether to show the back face */
  flipped?: boolean;
  /** Skip the flip transition (for instant display on page refresh) */
  skipTransition?: boolean;
  /** When true, fade out to transparent (crossfade with Board behind) */
  fadingOut?: boolean;
}

const FLIP_DURATION = 800;

// Column face positions within the 970×620 door area — must match Board's COLUMN_X_POSITIONS.
// Each face is 232×620 (brown), with 14px charcoal gaps between them.
const COL_FACE_POSITIONS = [0, 246, 492, 738];
const COL_FACE_W = 232;

// Base card dimensions (at scale 1.0)
const CARD_W = 380;
const CARD_H = 560;

/** Card overlay — rendered outside the board's transform: scale() for crisp text.
    Positioned to center over the board's mainBlock area in viewport coordinates. */
function CardOverlay({ left, top, scale, fadingOut, hasFlip, flipped, skipTransition, front, back }: {
  left: number; top: number; scale: number; fadingOut: boolean;
  hasFlip: boolean; flipped: boolean; skipTransition: boolean;
  front: ReactNode; back: ReactNode;
}) {
  // Card rendered at its natural 380×520 size, then CSS `zoom` scales it to
  // match the board. Unlike transform:scale, zoom re-rasterizes text at the
  // target size, so it stays crisp at any scale factor.
  // Position: zoom affects the coordinate system, so divide viewport coords by zoom.
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const cardZoom = isMobile ? scale * 1.5 : scale;
  const centerX = left + 500 * scale;
  const centerY = top + 510 * scale;
  const cardLeft = (centerX - (CARD_W * cardZoom) / 2) / cardZoom;
  const cardTop = (centerY - (CARD_H * cardZoom) / 2) / cardZoom;

  return (
    <div
      style={{
        position: 'fixed',
        left: cardLeft,
        top: cardTop,
        width: CARD_W,
        height: CARD_H,
        zoom: cardZoom,
        zIndex: 2,
        pointerEvents: 'auto',
        opacity: fadingOut ? 0 : 1,
        transition: fadingOut ? 'opacity 250ms ease-out' : 'none',
      }}
    >
      {hasFlip ? (
        <div style={{
          width: '100%',
          height: '100%',
          perspective: 2000,
          position: 'relative',
        }}>
          <div
            style={{
              width: '100%',
              height: '100%',
              position: 'relative',
              transformStyle: 'preserve-3d',
              transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
              transition: skipTransition ? 'none' : `transform ${FLIP_DURATION}ms ease-in-out`,
            }}
          >
            <div className={styles.flipFace}>{front}</div>
            <div className={`${styles.flipFace} ${styles.flipFaceBack}`}>{back}</div>
          </div>
        </div>
      ) : (
        <div style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          {front}
        </div>
      )}
    </div>
  );
}

function LoginBoard({ children, backChildren, flipped = false, skipTransition = false, fadingOut = false }: LoginBoardProps) {
  const { scale, left, top } = useBoardLayout();


  const hasFlip = backChildren !== undefined;

  return (
    <>
    <div
      style={{
        position: 'fixed',
        left,
        top,
        width: BOARD_WIDTH * scale,
        height: BOARD_HEIGHT * scale,
        overflow: 'hidden',
        background: 'none',
        zIndex: 2,
        opacity: fadingOut ? 0 : 1,
        transition: fadingOut ? 'opacity 250ms ease-out' : 'none',
        pointerEvents: fadingOut ? 'none' : 'auto',
      }}
    >
      <div
        style={{
          width: BOARD_WIDTH,
          height: BOARD_HEIGHT,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          position: 'relative',
        }}
      >
        {/* Static board chrome — never flips */}
        <BoardBackground />
        <div className={styles.bannerBlock}>
          {/* Trapezoid clips hide the brown shadow strips on the wider door SVGs */}
          <div style={{
            position: 'absolute', left: 3, top: 3, width: 564, height: 164,
            clipPath: "path('M0 164V0h564v0.013L400 164H0z')",
          }}>
            <LogoLeft />
          </div>
          <div style={{
            position: 'absolute', left: 403, top: 3, width: 564, height: 164,
            clipPath: "path('M564 0v164H0v-0.013L164 0H564z')",
          }}>
            <LogoRight style={{ left: -4 }} />
          </div>
          <PlusSign />
        </div>

        {/* Door rectangle — column face placeholders */}
        <div className={styles.doorRect}>
          {COL_FACE_POSITIONS.map((x, i) => (
            <div key={i} style={{ position: 'absolute', left: x, top: 0, width: COL_FACE_W, height: 620, backgroundColor: '#514b44' }}>
            </div>
          ))}
        </div>

        {/* Static footer */}
        <div className={styles.footerBlock}>
          <AGameOfAnalogies
            separated={false}
            style={{ position: 'absolute', left: 3, top: 3 }}
          />
          <RevealDoorFull />
        </div>
      </div>
    </div>

    <CardOverlay
      left={left}
      top={top}
      scale={scale}
      fadingOut={fadingOut}
      hasFlip={hasFlip}
      flipped={flipped}
      skipTransition={skipTransition}
      front={children}
      back={backChildren}
    />
    </>
  );
}

export default LoginBoard;
